import { FilterNbAirportOption } from '@/src/types/flight'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import useClevertapForSrp from '../../analytics/clevertap/srp'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import useSearchResults from '../useSearchResults'
export interface IncludeAirport extends FilterNbAirportOption {
    isSelected?: boolean
}

const useIncludeAirportsManager = (journeyLeg: 0 | 1 = 0) => {
    const t = useTranslations('srpFilters')
    const title = t('includedAirports')
    const { triggerSrpEvent } = useClevertapForSrp()

    const updateCache = useUpdateSearchResultCache()

    const { filterData, jsons, selectedJourneyLeg } = useSearchResults(journeyLeg)
    const { nbAirportFilter: nbAirportFilters } = filterData || {}
    const nbAirportFilter = nbAirportFilters?.[selectedJourneyLeg]
    const { selectedValues: nbAirportFilterSelectedValues } = nbAirportFilter || {}
    const airportsJson = jsons?.ap || {}
    const nbAirportOptions: IncludeAirport[] | undefined = useMemo(() => {
        return nbAirportFilter?.options.map(option => {
            const airport = airportsJson[option.value]
            return {
                label: `${airport.n}, ${airport.c} (${option.value})`,
                value: option.value,
                subLabel: option.subLabel,
                isSelected: nbAirportFilterSelectedValues?.includes(option.value) || false
            }
        })
    }, [nbAirportFilter, nbAirportFilterSelectedValues, airportsJson])

    const handleSelectToggle = useCallback(
        (id: string) => {
            const isRemoving = nbAirportFilterSelectedValues?.includes(id)
            const selectedValues = isRemoving
                ? nbAirportFilterSelectedValues?.filter(s => s !== id) || []
                : [...(nbAirportFilterSelectedValues || []), id]

            triggerSrpEvent('Air_UI_Action', {
                action_name: 'filter',
                action_type: 'nearby_airports',
                action_value: selectedValues.join(',') || 'none'
            })

            if (isRemoving) {
                //update rtk cache
                updateCache(currentData => {
                    if (currentData?.filterData?.nbAirportFilter?.[selectedJourneyLeg]) {
                        currentData.filterData.nbAirportFilter[selectedJourneyLeg].selectedValues =
                            nbAirportFilterSelectedValues.filter(s => s !== id)
                    }
                    return currentData as NonNullable<typeof currentData>
                })
            } else {
                updateCache(currentData => {
                    if (currentData?.filterData?.nbAirportFilter?.[selectedJourneyLeg]) {
                        currentData.filterData.nbAirportFilter[selectedJourneyLeg].selectedValues.push(id)
                    }
                    return currentData as NonNullable<typeof currentData>
                })
            }
        },
        [nbAirportFilterSelectedValues, selectedJourneyLeg, triggerSrpEvent]
    )
    const handleReset = useCallback(() => {
        triggerSrpEvent('Air_UI_Action', {
            action_name: 'filter',
            action_type: 'reset',
            action_value: 'nearby_airports'
        })

        updateCache(currentData => {
            if (currentData?.filterData?.nbAirportFilter?.[selectedJourneyLeg]) {
                currentData.filterData.nbAirportFilter[selectedJourneyLeg].selectedValues = []
            }
            return currentData as NonNullable<typeof currentData>
        })
    }, [selectedJourneyLeg, triggerSrpEvent])

    const shouldShowReset = useMemo(() => {
        return !!nbAirportFilterSelectedValues?.length
    }, [nbAirportFilterSelectedValues])

    return {
        title,
        nbAirportOptions,
        handleSelectToggle,
        handleReset,
        shouldShowReset
    }
}

export default useIncludeAirportsManager
